import { useEffect, useMemo, useState } from 'react';
import { planningApi } from './api';
import { PlanningResource, PlanningAssignment, PlanningPhase, RESOURCE_TYPE_LABEL } from './types';
import { addDaysISO, toISO, diffDaysISO, VISIBLE_DAYS, DAY_PX, GRID_WIDTH, monthGroupsFor, dayBarStyle, effectiveSegments, formatHours } from './dayGrid';

// Moltes assignacions antigues es van crear sense guardar `hours` (camp
// nul a la base de dades): cal recórrer a les hores de la fase, igual que
// fa el backend per detectar CAPACITAT_SUPERADA — si no, aquestes
// assignacions comptaven com 0h i el total sortia per sota del real.
function effectiveAssignmentHours(a: PlanningAssignment, phase: PlanningPhase | undefined): number {
  return a.hours != null ? Number(a.hours) : Number(phase?.plannedHours ?? 0);
}

// L'assignació es crea amb les hores totals de la fase (plannedHours), no
// amb la part que li toca: si hi ha 2 operaris assignats a la mateixa fase,
// cadascun guarda les 44h senceres i es duplicarien en mostrar-les — cal
// repartir-les entre el nombre d'assignacions d'aquesta fase abans de
// distribuir-les entre els dies del tram (mateix criteri que fa servir el
// backend per detectar CAPACITAT_SUPERADA).
function hoursForSegment(totalHours: number, a: PlanningAssignment, seg: { startDate: string; endDate: string }, phaseHeadcount: number): number {
  const segs = effectiveSegments(a);
  const totalDays = segs.reduce((sum, s) => sum + diffDaysISO(s.startDate, s.endDate) + 1, 0);
  if (totalDays === 0 || !totalHours) return 0;
  const hoursPerDay = (totalHours / Math.max(1, phaseHeadcount)) / totalDays;
  return hoursPerDay * (diffDaysISO(seg.startDate, seg.endDate) + 1);
}

// Dues franges de dates se solapen si cap de les dues acaba abans que
// comenci l'altra.
function rangesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

export default function ResourceViewTab() {
  const [resources, setResources] = useState<PlanningResource[]>([]);
  const [assignments, setAssignments] = useState<PlanningAssignment[]>([]);
  const [phases, setPhases] = useState<PlanningPhase[]>([]);
  const [loading, setLoading] = useState(true);
  const [rangeStart, setRangeStart] = useState(toISO(new Date()));

  useEffect(() => {
    setLoading(true);
    // Sense filtre de dates: si es filtrés per "a partir d'avui" (com abans),
    // una assignació ja passada (p.ex. d'un expedient ja acabat) desapareixia
    // del tot — aquí es carreguen totes i nomes es posiciona la finestra
    // visible amb `rangeStart`, igual que CronogramaGrid/GeneralTab.
    Promise.all([
      planningApi.listResources(undefined, 'active'),
      planningApi.listAssignments({}),
      planningApi.listPhases({}),
    ]).then(([r, a, p]) => {
      setResources(r);
      setAssignments(a);
      setPhases(p);
      const dates = a.map((x) => x.startDate).filter(Boolean).sort();
      if (dates.length > 0) setRangeStart((prev) => (prev > dates[0] ? dates[0] : prev));
    }).finally(() => setLoading(false));
  }, []);

  const phaseById = useMemo(() => new Map(phases.map((p) => [p.id, p])), [phases]);
  const phaseHeadcount = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of assignments) m.set(a.phaseId, (m.get(a.phaseId) ?? 0) + 1);
    return m;
  }, [assignments]);

  // Hores totals per recurs i dia (sumant totes les seves assignacions,
  // encara que siguin de projectes diferents) — una mateixa assignació
  // reparteix les seves hores a parts iguals entre tots els dies que
  // ocupa (vegeu hoursForSegment). Es fa servir per mostrar la càrrega
  // real del dia sota les barres, ja que aquestes es tapen entre si quan
  // hi ha més d'una assignació el mateix dia.
  const resourceDayTotals = useMemo(() => {
    const byResource = new Map<string, Map<string, number>>();
    for (const a of assignments) {
      const headcount = phaseHeadcount.get(a.phaseId) ?? 1;
      const totalHours = effectiveAssignmentHours(a, phaseById.get(a.phaseId));
      const segs = effectiveSegments(a);
      const totalDays = segs.reduce((sum, s) => sum + diffDaysISO(s.startDate, s.endDate) + 1, 0);
      if (totalDays === 0 || !totalHours) continue;
      const perDay = (totalHours / Math.max(1, headcount)) / totalDays;
      let map = byResource.get(a.resourceId);
      if (!map) { map = new Map(); byResource.set(a.resourceId, map); }
      for (const s of segs) {
        for (let d = s.startDate; d <= s.endDate; d = addDaysISO(d, 1)) {
          map.set(d, (map.get(d) ?? 0) + perDay);
        }
      }
    }
    return byResource;
  }, [assignments, phaseHeadcount, phaseById]);
  const todayIso = toISO(new Date());
  const monthGroups = monthGroupsFor(rangeStart);

  const overlappingIds = useMemo(() => {
    const conflicted = new Set<string>();
    const byResource = new Map<string, PlanningAssignment[]>();
    for (const a of assignments) {
      if (!byResource.has(a.resourceId)) byResource.set(a.resourceId, []);
      byResource.get(a.resourceId)!.push(a);
    }
    for (const list of byResource.values()) {
      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          const a = list[i], b = list[j];
          // Tram a tram, no el rang agregat: si una assignació té un buit
          // pel mig (cap de setmana saltat), aquell buit no ha de marcar
          // conflicte amb una altra assignació que caigui just allà.
          const overlaps = effectiveSegments(a).some((sa) => effectiveSegments(b).some((sb) => rangesOverlap(sa.startDate, sa.endDate, sb.startDate, sb.endDate)));
          if (overlaps) {
            conflicted.add(a.id);
            conflicted.add(b.id);
          }
        }
      }
    }
    return conflicted;
  }, [assignments]);

  const DayGridLines = () => (
    <div className="absolute inset-0 flex pointer-events-none" style={{ width: GRID_WIDTH }}>
      {Array.from({ length: VISIBLE_DAYS }).map((_, di) => {
        const dayIso = addDaysISO(rangeStart, di);
        const d = new Date(`${dayIso}T00:00:00`);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const isToday = dayIso === todayIso;
        return <div key={di} className={`shrink-0 border-r ${isToday ? 'bg-amber-50 border-amber-200' : `border-gray-100 ${isWeekend ? 'bg-gray-50' : ''}`}`} style={{ width: DAY_PX }} />;
      })}
    </div>
  );

  // Total d'hores del recurs per a cada dia, sota les barres — en vermell
  // quan supera les hores/dia del recurs (sobrecàrrega).
  const DayTotalsRow = ({ totals, dailyCapacity }: { totals: Map<string, number>; dailyCapacity: number }) => (
    <div className="absolute inset-x-0 bottom-0 flex pointer-events-none" style={{ width: GRID_WIDTH, height: 13 }}>
      {Array.from({ length: VISIBLE_DAYS }).map((_, di) => {
        const dayIso = addDaysISO(rangeStart, di);
        const total = totals.get(dayIso) ?? 0;
        if (total <= 0) return <div key={di} style={{ width: DAY_PX }} />;
        const over = total > dailyCapacity + 0.01;
        return (
          <div key={di} style={{ width: DAY_PX }} className="flex items-center justify-center">
            <span title={`Total del dia: ${formatHours(total)}h${over ? ` (capacitat: ${formatHours(dailyCapacity)}h)` : ''}`} className={`text-[8px] font-semibold leading-none ${over ? 'text-red-600' : 'text-gray-400'}`}>{formatHours(total)}h</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Càrrega de recursos</h3>
        <p className="text-[11px] text-muted-foreground">Les barres vermelles marquen assignacions que se solapen (conflicte)</p>
      </div>
      {loading ? (
        <div className="p-8 text-center text-xs text-muted-foreground">Carregant...</div>
      ) : resources.length === 0 ? (
        <div className="p-8 text-center text-xs text-muted-foreground">Sense recursos actius</div>
      ) : (
        <div>
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-[11px] text-muted-foreground">
              {new Date(`${rangeStart}T00:00:00`).toLocaleDateString('ca-ES')} — {new Date(`${addDaysISO(rangeStart, VISIBLE_DAYS - 1)}T00:00:00`).toLocaleDateString('ca-ES')}
            </span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setRangeStart(addDaysISO(rangeStart, -7))} className="text-[11px] px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">‹ setmana</button>
              <button onClick={() => setRangeStart(toISO(new Date()))} className="text-[11px] px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">avui</button>
              <button onClick={() => setRangeStart(addDaysISO(rangeStart, 7))} className="text-[11px] px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">setmana ›</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="flex" style={{ minWidth: `${220 + GRID_WIDTH}px` }}>
              {/* Esquerra: nom del recurs */}
              <div className="w-[220px] shrink-0 border-r border-gray-200">
                <div className="h-10 border-b border-gray-200 bg-gray-50" />
                {resources.map((r) => (
                  <div key={r.id} className="flex flex-col justify-center h-10 px-2 border-b border-gray-100">
                    <span className="text-xs font-medium text-gray-800 truncate">{r.name}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{RESOURCE_TYPE_LABEL[r.type]}</span>
                  </div>
                ))}
              </div>

              {/* Dreta: graella de dies */}
              <div className="shrink-0 relative" style={{ width: GRID_WIDTH }}>
                <div className="flex h-5 border-b border-gray-200 bg-gray-50" style={{ width: GRID_WIDTH }}>
                  {monthGroups.map((mg, mi) => (
                    <div key={mi} className="shrink-0 border-r border-gray-200 flex items-center justify-center text-[10px] font-semibold text-muted-foreground uppercase" style={{ width: mg.days * DAY_PX }}>{mg.label}</div>
                  ))}
                </div>
                <div className="flex h-5 border-b border-gray-200 bg-gray-50" style={{ width: GRID_WIDTH }}>
                  {Array.from({ length: VISIBLE_DAYS }).map((_, di) => {
                    const dayIso = addDaysISO(rangeStart, di);
                    const d = new Date(`${dayIso}T00:00:00`);
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const isToday = dayIso === todayIso;
                    return (
                      <div key={di} className={`shrink-0 border-r flex items-center justify-center text-[9px] font-semibold ${isToday ? 'bg-amber-200 border-amber-300 text-amber-900' : `border-gray-100 font-normal ${isWeekend ? 'bg-gray-100 text-muted-foreground/60' : 'text-muted-foreground'}`}`} style={{ width: DAY_PX }}>
                        {d.getDate()}
                      </div>
                    );
                  })}
                </div>

                {resources.map((r) => {
                  const list = assignments.filter((a) => a.resourceId === r.id);
                  return (
                    <div key={r.id} className="relative h-10 border-b border-gray-100" style={{ width: GRID_WIDTH }}>
                      <DayGridLines />
                      <DayTotalsRow totals={resourceDayTotals.get(r.id) ?? new Map()} dailyCapacity={Number(r.dailyHours) || 8} />
                      {list.map((a) => {
                        const phase = phaseById.get(a.phaseId);
                        const label = phase ? (phase.expedientLabel || phase.expedientCode || phase.name) : '';
                        const headcount = phaseHeadcount.get(a.phaseId) ?? 1;
                        const totalHours = effectiveAssignmentHours(a, phase);
                        return effectiveSegments(a).map((seg) => {
                          const hours = hoursForSegment(totalHours, a, seg, headcount);
                          const hoursText = hours > 0 ? `${formatHours(hours)}h` : '';
                          return (
                            <div
                              key={`${a.id}-${seg.id}`}
                              title={[label, hoursText].filter(Boolean).join(' · ')}
                              className={`absolute top-1 h-[22px] rounded px-1 flex items-center overflow-hidden ${overlappingIds.has(a.id) ? 'bg-red-500' : a.status === 'CONFIRMADA' ? 'bg-blue-600' : 'bg-blue-300'}`}
                              style={dayBarStyle(seg.startDate, seg.endDate, rangeStart)}
                            >
                              {label && <span className="text-[9px] text-white font-medium truncate leading-none">{label}{hoursText ? ` · ${hoursText}` : ''}</span>}
                            </div>
                          );
                        });
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
